import { useRouter } from "next/router";
import styled from "styled-components";
import { Flex, Padding } from "../../components/Grid";
import { Icon, IconNames } from "../../components/Icon";
import { Body, Footnote, Headline, Title2 } from "../../components/Typography";
import { Button } from "../../components/buttons/Button";
import { hideAboveMobile, hideOnMobile } from "../../lib/mediaQuery";

export type PlaceholderVariant =
  | "collectionOverview"
  | "collectionListed"
  | "collectionActivity"
  | "collectionActivityDropPage"
  | "collectionActivitySecondaryMkt"
  | "collectionNoEditions"
  | "creatorPage"
  | "landingPage"
  | "landingPageError";

interface PlaceholderProps {
  variant?: PlaceholderVariant;
  buttonCallback?: () => void;
  iconName?: IconNames;
  padding?: Padding;
  className?: string;
}

export const Placeholder = ({
  variant,
  buttonCallback,
  iconName = "Tracklist",
  padding = "xxl",
  className,
}: PlaceholderProps) => {
  const router = useRouter();
  let title: string,
    description: string,
    buttonText: string = "Discover Vaults",
    buttonDestination: Function = () => router.push("/"),
    showButton = true,
    iconColor = `rgba(var(--black-light))`;

  switch (variant) {
    case "creatorPage":
      title = "Nothing to See Here";
      description =
        "This Artist has no drops yet; head over to the Discover Page to browse Vaults.";
      break;
    case "collectionOverview":
      title = "All your music will appear here";
      description =
        "Start purchasing Vaults and begin building your music collection";
      break;
    case "collectionListed":
      title = "No Listings";
      description = "You currently have no Vaults listed for sale";
      iconName = "Label";
      showButton = false;
      break;
    case "collectionActivity":
      title = "No Activity";
      description = "Discover Vaults to start your collection now.";
      iconName = "Timeline";
      break;
    case "collectionActivityDropPage":
      title = "No Activity";
      description = "There is currently no activity for this Vault";
      iconName = "Graph";
      showButton = true;
      buttonText = "Buy Now";
      buttonDestination = buttonCallback;
      break;
    case "collectionActivitySecondaryMkt":
      title = "No Activity";
      description =
        "There is currently no Secondary Market activity for this Vault";
      iconName = "Graph";
      showButton = false;
      break;
    case "collectionNoEditions":
      title = "No Editions";
      description =
        "No copies of this Vault are currently available on the secondary market";
      iconName = "Label";
      iconColor = "inherit";
      showButton = false;
      break;
    case "landingPage":
      title = "No Results";
      description = "Please try another filter.";
      buttonText = "Clear Filters";
      buttonDestination = buttonCallback;
      break;
    case "landingPageError":
      title = "Something went wrong";
      description = "Please retry";
      buttonText = "Retry";
      buttonDestination = buttonCallback;
      break;
    default:
      title = "Drop Not Found";
      description = "The Drop you are looking for does not exist.";
  }

  return (
    <Container
      direction={"column"}
      justify={"center"}
      align={"center"}
      padding={padding}
      className={className}
    >
      {iconName === "Tracklist" ? (
        <>
          <Icon
            css={hideOnMobile}
            name={iconName}
            height={100}
            width={100}
            color={iconColor}
          />
          <Icon
            css={hideAboveMobile}
            name={iconName}
            height={70}
            width={70}
            color={iconColor}
          />
        </>
      ) : (
        <>
          <Flex paddingBottom={"md"}>
            <Icon
              css={hideOnMobile}
              name={iconName}
              height={80}
              width={80}
              color={iconColor}
            />
          </Flex>
          <Flex paddingBottom={"xxs"}>
            <Icon
              css={hideAboveMobile}
              name={iconName}
              height={60}
              width={60}
              color={iconColor}
            />
          </Flex>
        </>
      )}
      <Title2
        css={`
          text-align: center;
          ${hideOnMobile}
        `}
      >
        {title}
      </Title2>
      <Headline
        css={`
          text-align: center;
          ${hideAboveMobile}
        `}
      >
        {title}
      </Headline>
      <Body
        css={`
          color: rgb(var(--grey-dark));
          margin: var(--spacing-md) 0;
          max-width: 316px;
          text-align: center;
          ${hideOnMobile}
        `}
      >
        {description}
      </Body>
      <Footnote
        css={`
          color: rgb(var(--grey-dark));
          margin: var(--spacing-xs) 0;
          max-width: 316px;
          text-align: center;
          ${hideAboveMobile}
        `}
      >
        {description}
      </Footnote>
      {showButton && (
        <Button
          css={`
            width: 175px;
          `}
          shape={"rectangle"}
          onClick={() => buttonDestination()}
        >
          {buttonText}
        </Button>
      )}
    </Container>
  );
};

const Container = styled(Flex)``;
